import { Calendar as CalendarIcon } from 'lucide-react';
import { format, parse, isValid } from 'date-fns';
import { Checkbox, DatePicker } from '@scripsteam/scrips-signal-ds';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

/**
 * LMP (Last Menstrual Period) editor (PROD-795 PR 3a).
 *
 * Flutter parity:
 *   - `vital_cell_widget.dart` → `datePickerView()` for the date entry
 *   - `vitals_tab.dart:516-538` for the isMenstrual toggle logic
 *
 * Wire shape:
 *   - `quantity.value`: human-formatted date string `"May 04, 2026"`
 *     (Flutter's `MMM dd, yyyy` format — kept verbatim so dates round-
 *     trip without locale drift).
 *   - When the user toggles "Currently menstruating" OFF, Flutter sets
 *     value = `"Not menstruating"` and clears the picker. Toggling ON
 *     clears the value to null and shows the picker.
 *   - `isMenstrual` ships as a boolean field on the PatientVital row.
 *
 * Read-mode display: shows the value verbatim ("May 04, 2026", "Not
 * menstruating", or "—" when blank).
 */

export const NOT_MENSTRUATING_VALUE = 'Not menstruating';
const LMP_DATE_FORMAT = 'MMM dd, yyyy';

/**
 * Parse the wire value into a Date for the picker. Returns undefined
 * for blank values or the "Not menstruating" sentinel.
 */
export function parseLmpDateValue(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  if (value === NOT_MENSTRUATING_VALUE) return undefined;
  const parsed = parse(value, LMP_DATE_FORMAT, new Date());
  return isValid(parsed) ? parsed : undefined;
}

/** Format a Date as the LMP wire string. */
export function formatLmpDateValue(date: Date): string {
  return format(date, LMP_DATE_FORMAT);
}

interface LmpEditorProps {
  /** Current wire value (the formatted date OR "Not menstruating" OR null). */
  value: string;
  /** Mirrors PatientVital.isMenstrual on the LMP row. */
  isMenstrual: boolean;
  /** Fires when the user picks a date OR clears it. Receives the new
   *  wire value (formatted string or empty). */
  onValueChange: (next: string) => void;
  /** Fires when the user toggles "Currently menstruating". */
  onMenstrualChange: (next: boolean) => void;
  /**
   * Render the Flutter pre-encounter cell's THREE indicator rows instead of the single
   * "Currently menstruating" checkbox: No longer menstruating · Pregnancy indicator ·
   * Breast feeding (vital_cell_widget.dart:540-651). Opt-in — the appointment panel keeps
   * the single checkbox it shipped with.
   */
  showIndicators?: boolean;
  /** `PatientVital.isBreastFeeding` — a REAL wire field (vitals-api.ts:60). */
  isBreastFeeding?: boolean;
  onBreastFeedingChange?: (next: boolean) => void;
  /**
   * Pregnancy indicator. NO wire field exists for this on either platform — Flutter stores it
   * in `AppPreferenceUtil` under a GLOBAL key (vital_cell_widget.dart:591), i.e. device-wide
   * and NOT per patient, so toggling it for one patient shows it lit for the next. That is a
   * bug we deliberately do not reproduce: here it is form-local state, scoped to the encounter
   * being edited, and it does NOT persist. See the note in encounter-workflow.tsx.
   */
  isPregnant?: boolean;
  onPregnantChange?: (next: boolean) => void;
}

export function LmpEditor({
  value,
  isMenstrual,
  onValueChange,
  onMenstrualChange,
  showIndicators = false,
  isBreastFeeding = false,
  onBreastFeedingChange,
  isPregnant = false,
  onPregnantChange,
}: LmpEditorProps) {
  const selected = parseLmpDateValue(value);
  const showPicker = isMenstrual; // hide picker when not menstruating

  return (
    <div className="lmp-root">
      {showPicker ? (
        <DatePicker
          // Note: shared/ui/components/date-picker-field.tsx keys on its
          // value to force a remount, because the DS DatePicker's `commit`
          // sets its OWN `internal` state whenever the caller's `value` is
          // undefined (`if (value === undefined) setInternal(d)`), and that
          // internal state does NOT clear when `value` later goes back to
          // undefined — the calendar's selected-day fill and its footer date
          // go stale.
          //
          // It IS reachable here, which is why this key stays. react-day-picker
          // in mode="single" emits `undefined` when you click the ALREADY
          // SELECTED day, and the DS's own handler is
          // `(d) => { commit(d); if (d) setOpen(false); }` — so a deselect
          // deliberately leaves the popover OPEN and MOUNTED while this
          // editor's onChange below clears the value to ''. The picker then
          // flips to uncontrolled mid-life with a stale `internal`.
          // Keying on the value forces a remount instead. Pinned by the
          // "deselecting the current day" test.
          key={value || 'empty'}
          value={selected}
          onChange={(next) => {
            if (next) {
              onValueChange(formatLmpDateValue(next));
            } else {
              onValueChange('');
            }
          }}
          maxDate={new Date()}
          dateFormat={LMP_DATE_FORMAT}
          trigger={
            <button
              type="button"
              className="lmp-trigger"
              data-set={selected ? '' : undefined}
              aria-label="Last menstrual period date"
            >
              <span>{selected ? formatLmpDateValue(selected) : 'Select date'}</span>
              <CalendarIcon className="size-4" aria-hidden />
            </button>
          }
        />
      ) : (
        // Use the exported constant for the visible label too, so a
        // sentinel rename can't drift the displayed string out of
        // sync with the wire value. Samer review PR #154 §1.
        <p className="lmp-not">
          {NOT_MENSTRUATING_VALUE}
        </p>
      )}

      {showIndicators ? (
        <div className="lmp-rows">
          {/* Flutter's row reads "No longer menstruating" and is the INVERSE of isMenstrual
              (vital_cell_widget.dart:541-566) — the same field, stated the other way round. */}
          <IndicatorRow
            label="No longer menstruating"
            checked={!isMenstrual}
            onChange={(noLonger) => {
              const menstruating = !noLonger;
              onMenstrualChange(menstruating);
              if (!menstruating) {
                onValueChange(NOT_MENSTRUATING_VALUE);
              } else if (value === NOT_MENSTRUATING_VALUE) {
                onValueChange('');
              }
            }}
          />
          <IndicatorRow
            label="Pregnancy indicator"
            checked={isPregnant}
            onChange={(next) => onPregnantChange?.(next)}
            disabled={!onPregnantChange}
          />
          <IndicatorRow
            label="Breast feeding"
            checked={isBreastFeeding}
            onChange={(next) => onBreastFeedingChange?.(next)}
            disabled={!onBreastFeedingChange}
          />
        </div>
      ) : (
        // The design's DS Checkbox (PaLmpEditor): same field, same Flutter `vitals_tab.dart:516-538` rule — clearing
        // it stamps the sentinel value; setting it clears the picker so the user can enter a date.
        <Checkbox
          checked={isMenstrual}
          onCheckedChange={(checked) => {
            const next = checked;
            onMenstrualChange(next);
            if (!next) {
              onValueChange(NOT_MENSTRUATING_VALUE);
            } else if (value === NOT_MENSTRUATING_VALUE) {
              onValueChange('');
            }
          }}
          label={<span className="lmp-cb">Currently menstruating</span>}
        />
      )}
    </div>
  );
}

/** One indicator row: label left, switch right — Flutter's 27x16 SizedCupertinoSwitch. */
function IndicatorRow({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className="lmp-row"
      data-disabled={disabled || undefined}
    >
      <span className="truncate">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className="lmp-sw"
        data-on={checked || undefined}
      >
        <span className="lmp-knob" />
      </button>
    </label>
  );
}
